/**
 * Live free-trial length, configurable by an admin.
 *
 * Reads `system_config/trial` → `{ days: number }`. Falls back to the
 * TRIAL_DAYS constant while loading or if the doc/field is missing, so the UI
 * never shows an empty/zero trial by accident.
 *
 * The doc is world-readable (see firestore.rules) because guests see the trial
 * length on the public /business and /pricing pages.
 */
'use client';
import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/client';
import { TRIAL_DAYS, normalizeTrialDays } from '@/lib/subscription';

export function useTrialConfig(): { trialDays: number; trialMonths: number; loading: boolean } {
  const [trialDays, setTrialDays] = useState<number>(TRIAL_DAYS);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'system_config', 'trial'));
        if (mounted && snap.exists() && snap.data()?.days !== undefined) {
          setTrialDays(normalizeTrialDays(snap.data()?.days));
        }
      } catch {
        // Keep the default on error — never block the UI.
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);
  return {
    trialDays,
    trialMonths: Math.max(1, Math.round(trialDays / 30)),
    loading,
  };
}
